import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { DoorCategory } from "../types";

export type DoorEvidenceEntry = {
  strength: number;
  pains: string[];
  evidence: string;
};

export type DoorEvidence = Record<string, DoorEvidenceEntry>;

export type LeadScoreRow = {
  score_type: "icp_fit" | "intent" | "composite" | string;
  current_score: number | null;
  score_band: string | null;
  pain_tags: string[] | null;
  entry_point: string | null;
  rationale: string | null;
  intent_components: Record<string, unknown> | null;
};

export type MarcoDoorDetail = {
  primary_door: DoorCategory | null;
  secondary_door: DoorCategory | null;
  ambiguity_note: string | null;
  confidence: "high" | "medium" | "low" | null;
  rationale: string | null;
  door_evidence: DoorEvidence | null;
  scores: LeadScoreRow[];
};

/**
 * PRD #16 §E — Marco card data. Reads mentorax.lead_doors + mentorax.lead_scores
 * directly (not the lead_snapshots read-model, which deliberately doesn't carry
 * this level of detail) via the same cross-schema client pattern useMarcoActivity.ts
 * already established. Returns null data (not an error) when the contact has no
 * lead_doors row yet — that's the empty state, not a failure.
 */
export function useMarcoDoorDetail(contactId: number | undefined) {
  return useQuery({
    queryKey: ["marco-door-detail", contactId],
    enabled: !!contactId,
    queryFn: async (): Promise<MarcoDoorDetail | null> => {
      const mentorax = getSupabaseClient().schema("mentorax");

      const [doorsRes, scoresRes] = await Promise.all([
        mentorax
          .from("lead_doors")
          .select(
            "door_category, secondary_door_category, ambiguity_note, confidence, rationale, door_evidence, assigned_at",
          )
          .eq("contact_id", contactId)
          .order("assigned_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        mentorax
          .from("lead_scores")
          .select(
            "score_type, current_score, score_band, pain_tags, entry_point, rationale, intent_components",
          )
          .eq("contact_id", contactId),
      ]);

      if (doorsRes.error) throw doorsRes.error;
      if (scoresRes.error) throw scoresRes.error;

      if (!doorsRes.data) return null;

      return {
        primary_door: (doorsRes.data.door_category as DoorCategory) ?? null,
        secondary_door:
          (doorsRes.data.secondary_door_category as DoorCategory) ?? null,
        ambiguity_note: doorsRes.data.ambiguity_note ?? null,
        confidence: (doorsRes.data.confidence as MarcoDoorDetail["confidence"]) ?? null,
        rationale: doorsRes.data.rationale ?? null,
        door_evidence: (doorsRes.data.door_evidence as DoorEvidence) ?? null,
        scores: (scoresRes.data ?? []) as LeadScoreRow[],
      };
    },
  });
}
